import React, { useState, useRef, useEffect, ReactNode } from 'react';
import { Typography as MUITypography, TypographyProps as MUITypographyProps, Tooltip } from '@mui/material';

interface UseOverflowHook {
  ref: React.RefObject<HTMLSpanElement>;
  isOverflowing: boolean;
}

const useOverflow = (): UseOverflowHook => {
  const ref = useRef<HTMLSpanElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const checkOverflow = () => {
    const element = ref.current;
    if (element) {
      // Check if the content's width exceeds the element's bounding box
      setIsOverflowing(element.scrollWidth > element.clientWidth);
    }
  };

  useEffect(() => {
    checkOverflow();
    window.addEventListener('resize', checkOverflow); // Add resize listener to update overflow status on window resize
    return () => {
      window.removeEventListener('resize', checkOverflow); // Cleanup listener on component unmount
    };
  }, []);

  return { ref, isOverflowing };
};

interface TypographyProps extends MUITypographyProps {
  children: ReactNode;
}

const Typography: React.FC<TypographyProps> = ({ children, sx, ...otherProps }) => {
  const { ref, isOverflowing } = useOverflow();
  return (
    <Tooltip title={typeof children === 'string' ? children : ''} disableHoverListener={!isOverflowing} placement="top">
      <MUITypography ref={ref} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', ...sx }} {...otherProps}>
        {children}
      </MUITypography>
    </Tooltip>
  );
};

export default Typography;
